const DEFAULT_TRANSFORMATION = {
    originOffset: true,
    originX: 0,
    originY: 0,
    translateX: 0,
    translateY: 0,
    scale: 1,
};

const getMatrix = ({ scale, translateX, translateY }) =>
    `matrix(${scale}, 0, 0, ${scale}, ${translateX}, ${translateY})`;

const clamp = (value, min, max) => Math.max(Math.min(value, max), min);

const clampedTranslate = ({ axis, translate, state }) => {
    const { scale, originX, originY } = state.transform;
    const axisIsX = axis === "x";
    const origin = axisIsX ? originX : originY;
    const axisKey = axisIsX ? "offsetWidth" : "offsetHeight";
    const containerSize = state.container[axisKey];
    const imageSize = state.element[axisKey];
    const bounds = state.element.getBoundingClientRect();

    const imageScaledSize = axisIsX ? bounds.width : bounds.height;

    const defaultOrigin = imageSize / 2;
    const originOffset = (origin - defaultOrigin) * (scale - 1);

    const range = Math.max(0, Math.round(imageScaledSize) - containerSize);

    const max = Math.round(range / 2);
    const min = 0 - max;

    return clamp(translate, min + originOffset, max + originOffset);
};

const renderClamped = ({ state, translateX, translateY }) => {
    const { originX, originY, scale } = state.transform;

    state.transform.translateX = clampedTranslate({
        axis: "x",
        translate: translateX,
        state,
    });
    state.transform.translateY = clampedTranslate({
        axis: "y",
        translate: translateY,
        state,
    });

    requestAnimationFrame(() => {
        if (state.transform.originOffset) {
            state.element.style.transformOrigin = `${originX}px ${originY}px`;
        }

        state.element.style.transform = getMatrix({
            scale,
            translateX: state.transform.translateX,
            translateY: state.transform.translateY,
        });
    });
};

const pan = (state, { originX, originY }) => {
    renderClamped({
        state,
        translateX: state.transform.translateX + originX,
        translateY: state.transform.translateY + originY,
    });
};

const canPan = (state) => ({
    panBy: (origin) => pan(state, origin),
    panTo: ({ originX, originY }) => {
        pan(state, {
            originX: originX - state.transform.translateX,
            originY: originY - state.transform.translateY,
        });
    },
});

const canInspect = (state) => ({
    reset: () => {
        pan(state, { originX: 0, originY: 0 });
        state.transform = DEFAULT_TRANSFORMATION;
    },
    getState: () => state,
});

export const renderer = ({ element, container }) => {
    const state = {
        container,
        element,
        transform: DEFAULT_TRANSFORMATION,
    };

    // Calculate origin offset
    const bounds = container.getBoundingClientRect();
    state.transform.originX = bounds.width / 2;
    state.transform.originY = bounds.height / 2;
    element.style.transformOrigin = `${state.transform.originX}px ${state.transform.originY}px`;

    return {
        ...canPan(state),
        ...canInspect(state),
    };
};
